<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import SubcategoryForm from './SubcategoryForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  category: { type: Object, required: true },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();

const showModal = ref(false);
const editingSubcategory = ref(null);

const priorityColors = {
  1: 'bg-g-100 text-g-800',
  2: 'bg-b-100 text-b-800',
  3: 'bg-y-100 text-y-800',
  4: 'bg-r-100 text-r-800',
};

const priorityLabels = {
  1: 'Baja',
  2: 'Media',
  3: 'Alta',
  4: 'Urgente',
};

const openNew = () => {
  editingSubcategory.value = null;
  showModal.value = true;
};

const openEdit = subcategory => {
  editingSubcategory.value = { ...subcategory };
  showModal.value = true;
};

const handleSaved = () => {
  showModal.value = false;
  editingSubcategory.value = null;
  emit('updated');
};

const deleteSubcategory = async subcategory => {
  if (
    !window.confirm(
      t('TICKET_SUBCATEGORIES.DELETE_CONFIRM', { name: subcategory.name })
    )
  ) {
    return;
  }

  try {
    await TicketCategoriesAPI.deleteSubcategory(
      props.category.id,
      subcategory.id
    );
    useAlert(t('TICKET_SUBCATEGORIES.DELETE_SUCCESS'));
    emit('updated');
  } catch (error) {
    const message =
      error.response?.data?.error || t('TICKET_SUBCATEGORIES.DELETE_ERROR');
    useAlert(message);
  }
};
</script>

<template>
  <div class="border-t border-n-weak">
    <!-- Subcategory items -->
    <div class="divide-y divide-n-weak">
      <div
        v-for="sub in category.ticket_subcategories"
        :key="sub.id"
        class="flex items-center justify-between px-4 py-3 pl-8 hover:bg-n-alpha-1"
      >
        <div class="flex items-center gap-3">
          <span class="font-mono text-xs text-n-slate-10">{{ sub.code }}</span>
          <span class="text-sm text-n-slate-12">{{ sub.name }}</span>
          <span
            class="text-xs px-2 py-0.5 rounded"
            :class="priorityColors[sub.default_priority]"
          >
            {{ priorityLabels[sub.default_priority] }}
          </span>
          <span
            v-if="sub.tipo"
            class="text-xs px-2 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ sub.tipo.description }}
          </span>
          <span
            v-if="sub.requires_contract"
            class="text-xs text-n-slate-10"
            :title="$t('TICKET_SUBCATEGORIES.REQUIRES_CONTRACT')"
          >
            <fluent-icon icon="document" size="14" />
          </span>
          <span
            v-if="sub.requires_location"
            class="text-xs text-n-slate-10"
            :title="$t('TICKET_SUBCATEGORIES.REQUIRES_LOCATION')"
          >
            <fluent-icon icon="location" size="14" />
          </span>
          <span v-if="sub.sla_hours" class="text-xs text-n-slate-10">
            <fluent-icon icon="clock" size="14" class="inline" />
            {{ sub.sla_hours }}h
          </span>
        </div>
        <div class="flex items-center gap-1">
          <Button
            v-tooltip.top="$t('TICKET_SUBCATEGORIES.EDIT')"
            icon="i-lucide-pen"
            slate
            xs
            faded
            @click="openEdit(sub)"
          />
          <Button
            v-tooltip.top="$t('TICKET_SUBCATEGORIES.DELETE')"
            icon="i-lucide-trash-2"
            xs
            ruby
            faded
            @click="deleteSubcategory(sub)"
          />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!category.ticket_subcategories?.length"
      class="p-4 pl-8 text-sm text-n-slate-10"
    >
      {{ $t('TICKET_SUBCATEGORIES.EMPTY') }}
    </div>

    <!-- Add button -->
    <div class="p-3 pl-8">
      <Button
        icon="i-lucide-plus"
        slate
        xs
        faded
        :label="$t('TICKET_SUBCATEGORIES.ADD')"
        @click="openNew"
      />
    </div>

    <!-- Modal -->
    <woot-modal v-model:show="showModal" :on-close="() => (showModal = false)">
      <SubcategoryForm
        :category="category"
        :subcategory="editingSubcategory"
        @saved="handleSaved"
        @cancel="showModal = false"
      />
    </woot-modal>
  </div>
</template>
